import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { ApiUsageService } from '../_services/api-usage.service';
import { UsersService } from '../_services/users.service';
import {
  ApiUsageDailyPoint,
  ApiUsageEndpoint,
  ApiUsageHourlyPoint,
  ApiUsageHourOfDayPoint,
  ApiUsageLogFilters,
  ApiUsageLogItem,
  ApiUsageStatusFilter,
  ApiUsageStatusSlice,
  ApiUsageSummary
} from '../_types/api-usage';
import { User } from '../_types/user';

@Component({
    selector: 'app-users-api-usage',
    templateUrl: './users-api-usage.component.html',
    styleUrls: ['./users-api-usage.component.scss'],
    standalone: false
})
export class UsersApiUsageComponent implements OnInit {

  /// Código del tipo de usuario que se registra. Solo se guardan las peticiones de estos usuarios, así que
  /// para el resto el panel no tiene nada que mostrar y hay que decirlo en lugar de dejarlo vacío.
  private static readonly API_USER_TYPE_CODE = 'api';

  public userId: string = '';
  public user: User | null = null;
  public isApiUser: boolean = false;
  public userLoaded: boolean = false;

  //rango de fechas
  public dateFrom: Date = new Date();
  public dateTo: Date = new Date();
  public rangeOptions: any[] = [
    { value: 1, name: 'Hoy' },
    { value: 7, name: 'Últimos 7 días' },
    { value: 30, name: 'Últimos 30 días' },
    { value: 90, name: 'Últimos 90 días' }
  ];
  /// El panel abre en el día de hoy: lo que se viene a mirar es qué está haciendo ahora mismo la
  /// integración, y para eso el rango largo diluye el tráfico del día en la media del mes.
  public rangeDays: number = 1;

  //datos
  public summary: ApiUsageSummary | null = null;
  public daily: ApiUsageDailyPoint[] = [];
  public statusSlices: ApiUsageStatusSlice[] = [];
  public topEndpoints: ApiUsageEndpoint[] = [];
  public statsLoading: boolean = false;

  //registro
  public logItems: ApiUsageLogItem[] = [];
  public logTotal: number = 0;
  public logLoading: boolean = false;
  private lastGridEvent: any;

  public logFilters: ApiUsageLogFilters = this.getDefaultLogFilters();

  //detalle de la respuesta de una petición fallida
  public responseVisible: boolean = false;
  public responseItem: ApiUsageLogItem | null = null;
  public responseBody: string = '';
  public responseNote: string = '';
  public responseLoading: boolean = false;

  /// Espejo de ApiUsage:MaxErrorResponseLength: con ese tamaño exacto la respuesta puede venir cortada, y
  /// hay que decirlo para que nadie dé por buena una respuesta que en realidad seguía.
  private static readonly MAX_RESPONSE_LENGTH = 4000;
  public methodOptions: any[] = [
    { value: null, name: 'Cualquiera' },
    { value: 'GET', name: 'GET' },
    { value: 'POST', name: 'POST' },
    { value: 'PATCH', name: 'PATCH' },
    { value: 'PUT', name: 'PUT' },
    { value: 'DELETE', name: 'DELETE' }
  ];
  /// Filtro de código de respuesta. Es general de la pantalla, como el periodo: lo aplican los indicadores,
  /// las gráficas, los endpoints y el registro. El valor lleva codificado a qué se filtra, porque el mismo
  /// desplegable ofrece clases y códigos: 'class:4' son todos los 4xx y 'code:404' solo el 404.
  public statusFilter: string | null = null;

  /// Opciones del filtro, agrupadas en clases y códigos. Se construyen con el reparto del periodo, que se
  /// pide siempre sin filtrar: así el desplegable ofrece solo lo que existe y no los 60 códigos de HTTP.
  public statusFilterOptions: any[] = [];

  /// Nombre de cada clase, para las opciones del filtro y para el rótulo del filtro activo.
  private static readonly STATUS_CLASS_NAMES: any = {
    '2': '2xx correctas',
    '3': '3xx redirecciones',
    '4': '4xx errores de petición',
    '5': '5xx errores de servidor'
  };

  /// Con un rango de varios días la media enseña el día tipo; el total sirve para el volumen.
  public seriesModeOptions: any[] = [
    { value: 'requests', name: 'Peticiones' },
    { value: 'errors', name: 'Errores' },
    { value: 'duration', name: 'Duración media' }
  ];
  public seriesMode: string = 'requests';

  /// Granularidad del eje X. La serie elegida (peticiones/errores/duración) es ortogonal a esto.
  public granularityOptions: any[] = [
    { value: 'day', name: 'Por día', disabled: false },
    { value: 'hour', name: 'Por hora', disabled: false },
    { value: 'hourOfDay', name: 'Por hora del día', disabled: false }
  ];
  /// Arranca por hora para acompañar al rango de un día: un rango de hoy en "Por día" es una sola columna.
  /// Si el periodo no admite el desglose horario, applyGranularityOptionState devuelve a "Por día".
  public granularity: string = 'hour';

  /// Espejo de MaxHourlySeriesDays en ApiRequestService: sirve para deshabilitar la opción en lugar de
  /// pedir en vano una serie que el servidor va a rechazar por larga.
  private static readonly MAX_HOURLY_SERIES_DAYS = 31;

  /// Por encima de este número de puntos las columnas se quedan en un píxel y la gráfica pasa a línea.
  private static readonly MAX_BAR_POINTS = 48;

  public hourlySeries: ApiUsageHourlyPoint[] = [];
  public hourOfDay: ApiUsageHourOfDayPoint[] = [];

  /// La vista horaria que toque se pide al seleccionarla, así que hay un momento sin datos que ocupar.
  public granularityLoading: boolean = false;

  /// Tipo de gráfica que toca a la granularidad y el número de puntos actuales. El tipo va estrecho y no
  /// como string porque el [type] de p-chart es una unión de literales y un string genérico no compila.
  public evolutionChartType: 'bar' | 'line' = 'bar';

  /// Caché de lo ya pedido para el rango actual: evita volver al servidor al conmutar de granularidad ida
  /// y vuelta. Se levantan también cuando la petición falla, para no reintentarla en cada conmutación.
  private hourlySeriesLoaded: boolean = false;
  private hourOfDayLoaded: boolean = false;

  /// Secuencia de la carga vigente. Cada Buscar y cada conmutación la incrementa, y una respuesta que
  /// vuelve con una secuencia caducada se descarta. Sin esto, una serie horaria en vuelo cuando se pulsa
  /// Buscar aterriza sobre el rango nuevo y encima deja su bandera de caché levantada, con lo que el panel
  /// se queda mostrando en silencio las horas del periodo anterior junto a los totales del nuevo.
  private granularityRequest: number = 0;

  /// Se intentó cargar la vista horaria y no pudo ser: fallo de red, o el servidor dice que ese periodo no
  /// puede servir la hora. Sin esto el fallo es indistinguible de "no hubo tráfico".
  private hourlyError: boolean = false;
  private hourOfDayError: boolean = false;

  public evolutionChartData: any = null;
  public evolutionChartOptions: any = null;
  public statusChartData: any = null;
  public statusChartOptions: any = null;

  /// Las porciones en el mismo orden en que se pintan: la tarta se ordena por volumen, así que el índice
  /// que devuelve el clic solo se puede traducir a código con esta lista.
  private statusChartSlices: ApiUsageStatusSlice[] = [];

  /// Rampa de un solo tono para magnitud: más oscuro cuanto más alta la columna. Misma que en el panel de
  /// registro de actividad, para que los dos paneles se lean igual.
  private static readonly SEQUENTIAL_LOW = [158, 197, 244];
  private static readonly SEQUENTIAL_HIGH = [28, 92, 171];

  /// La línea va a un solo color, el extremo alto de la rampa: en una línea el degradado por magnitud no
  /// comunica nada, porque no hay una superficie por punto que colorear.
  private static readonly LINE_COLOR = 'rgb(28,92,171)';

  /// Colores del desglose por estado. Aquí el color sí es identidad y no magnitud, y se asigna por
  /// significado: verde correcto, azul redirección, ámbar error del cliente, naranja error del servidor.
  /// Salen de la misma paleta validada para daltonismo que usa el panel de actividad.
  private static readonly STATUS_COLORS: any = {
    '2': '#1baf7a',
    '3': '#2a78d6',
    '4': '#eda100',
    '5': '#eb6834',
    '0': '#8a94a2'
  };

  constructor(
    private apiUsageService: ApiUsageService,
    private usersService: UsersService,
    private topbarService: TopbarService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    this.topbarService.setWindowTitle('Uso de API');
    this.topbarService.setTitle('Uso de API');
    this.topbarService.addBreadcrumb('Usuarios', '/users');

    this.userId = this.route.snapshot.paramMap.get('guid') ?? '';
    this.applyRange(this.rangeDays, false);

    this.loadUser();
  }

  /// Carga el usuario para las migas de pan y para saber si es de tipo API. Hasta que no se sabe no se
  /// pide nada más: pedir estadísticas de un usuario que no se registra sería tráfico para nada.
  async loadUser() {
    try {
      //los sparse fieldsets son obligatorios aquí: sin ellos la respuesta trae el usuario completo, con
      //Password incluido. userType va en la lista porque una relación que no se pide también se excluye.
      const user = await firstValueFrom(this.usersService.get(this.userId, {
        include: ['userType'],
        fields: { users: 'name,userType', userTypes: 'code,name' }
      }));
      this.user = user;
      if (user?.name) this.topbarService.addBreadcrumb(user.name, '/users/' + this.userId);
      this.isApiUser = user?.userType?.code == UsersApiUsageComponent.API_USER_TYPE_CODE;
    } catch (error) {
      //sin el usuario no se puede decidir si mostrar el panel; se avisa en pantalla
      console.warn('No se ha podido cargar el usuario', error);
      this.isApiUser = false;
    } finally {
      this.userLoaded = true;
      if (this.isApiUser) this.loadStats();
    }
  }

  getDefaultLogFilters(): ApiUsageLogFilters {
    return { method: null, routeTemplate: null, minDurationMs: null };
  }

  /// Aplica un atajo de rango. Las fechas se mandan en UTC y el agregado diario se consolida por día UTC,
  /// así que el rango se calcula sobre el día natural y el panel avisa de que los días son UTC.
  applyRange(days: number, reload: boolean = true) {
    const today = new Date();
    this.dateTo = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    this.dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1), 0, 0, 0, 0);

    if (reload) this.search();
  }

  onRangeChange() {
    this.applyRange(this.rangeDays);
  }

  async loadStats() {
    this.statsLoading = true;

    //las vistas horarias se piden aparte y solo si se usan, así que al cambiar de rango hay que olvidar
    //las que estuvieran en caché
    this.hourlySeries = [];
    this.hourOfDay = [];
    this.hourlySeriesLoaded = false;
    this.hourOfDayLoaded = false;
    this.hourlyError = false;
    this.hourOfDayError = false;
    //invalida cualquier vista horaria en vuelo: sus datos son ya del rango anterior
    this.granularityRequest++;

    try {
      const [summary, daily, statusSlices, topEndpoints] = await Promise.all([
        firstValueFrom(this.apiUsageService.getSummary(this.userId, this.dateFrom, this.dateTo, this.statusFilterQuery)),
        firstValueFrom(this.apiUsageService.getDaily(this.userId, this.dateFrom, this.dateTo, this.statusFilterQuery)),
        firstValueFrom(this.apiUsageService.getStatusCodes(this.userId, this.dateFrom, this.dateTo)),
        firstValueFrom(this.apiUsageService.getTopEndpoints(this.userId, this.dateFrom, this.dateTo, 10, this.statusFilterQuery))
      ]);

      this.summary = summary;
      this.daily = daily;
      this.statusSlices = statusSlices;
      this.topEndpoints = topEndpoints;
    } catch (error) {
      //las estadísticas son informativas: si fallan, la tabla de registro debe seguir funcionando
      console.warn('No se han podido cargar las estadísticas de uso de la API', error);
      this.summary = null;
      this.daily = [];
      this.statusSlices = [];
      this.topEndpoints = [];
    } finally {
      this.statsLoading = false;
      this.buildStatusFilterOptions();
      this.applyGranularityOptionState();
      this.buildStatusChart();
      //construye la gráfica de evolución, pidiendo la vista horaria si es la granularidad activa
      await this.loadGranularity();
    }
  }

  /// Deshabilita las granularidades que el periodo no puede servir y devuelve a "Por día" si la activa
  /// acaba de dejar de ser válida. Se deshabilitan en lugar de dejar que aterricen en una gráfica vacía:
  /// una gráfica en blanco sin explicación se lee como un error del panel.
  private applyGranularityOptionState() {
    const hourly = this.isHourlySeriesAvailable;
    const profile = this.isHourOfDayAvailable;

    //se muta la lista que ya existe en lugar de reconstruirla: los rótulos viven en un solo sitio y así no
    //pueden derivar entre las dos copias
    for (const option of this.granularityOptions) {
      if (option.value == 'hour') option.disabled = !hourly;
      if (option.value == 'hourOfDay') option.disabled = !profile;
    }

    if (this.granularity == 'hour' && !hourly) this.granularity = 'day';
    if (this.granularity == 'hourOfDay' && !profile) this.granularity = 'day';
  }

  /// Carga lo que falte para la granularidad activa y reconstruye la gráfica. Las dos vistas horarias se
  /// piden la primera vez que se seleccionan y después se reutilizan hasta el siguiente Buscar; la serie
  /// diaria ya viene de la carga inicial, así que volver a "Por día" nunca vuelve al servidor.
  public async loadGranularity() {
    //la granularidad se captura al entrar: this.granularity puede cambiar durante el await —el usuario
    //conmuta— y esta invocación tiene que seguir trabajando para la que la disparó, no para la nueva
    const granularity = this.granularity;
    const request = ++this.granularityRequest;

    if (granularity == 'hour' && !this.hourlySeriesLoaded) {
      const result = await this.fetchHourlyView(
        this.apiUsageService.getHourlySeries(this.userId, this.dateFrom, this.dateTo, this.statusFilterQuery),
        'la serie por hora');

      //una respuesta que llega cuando ya hay otra carga en curso no se pinta ni marca la caché: sus datos
      //son de un rango o de una granularidad que la pantalla ya ha dejado atrás. Quien la haya adelantado
      //es el dueño de granularityLoading y lo bajará él, así que aquí se sale sin tocar nada
      if (request != this.granularityRequest) return;

      this.hourlySeries = result.points;
      this.hourlySeriesLoaded = true;
      this.hourlyError = result.failed;
    }

    if (granularity == 'hourOfDay' && !this.hourOfDayLoaded) {
      const result = await this.fetchHourlyView(
        this.apiUsageService.getHourOfDay(this.userId, this.dateFrom, this.dateTo, this.statusFilterQuery),
        'el perfil por hora del día');

      if (request != this.granularityRequest) return;

      this.hourOfDay = result.points;
      this.hourOfDayLoaded = true;
      this.hourOfDayError = result.failed;
    }

    this.granularityLoading = false;
    this.buildEvolutionChart();
  }

  /// Pide una vista horaria y normaliza el resultado. Un source distinto de 'detail' cuenta como fallo:
  /// las dos cosas acaban en una gráfica sin puntos, y el panel tiene que poder decir "no se ha podido
  /// cargar", que no es lo mismo que "no hubo tráfico".
  private async fetchHourlyView<T>(request: Observable<{ points: T[], source: string }>, name: string):
    Promise<{ points: T[], failed: boolean }> {

    this.granularityLoading = true;

    try {
      const response = await firstValueFrom(request);
      const failed = response.source != 'detail';
      return { points: failed ? [] : response.points, failed: failed };
    } catch (error) {
      console.warn('No se ha podido cargar ' + name, error);
      return { points: [], failed: true };
    }
  }

  onGranularityChange() {
    this.loadGranularity();
  }

  public async loadLog($event: any) {
    this.lastGridEvent = $event;

    if (!this.isApiUser) {
      this.logItems = [];
      this.logTotal = 0;
      return;
    }

    this.logLoading = true;
    try {
      //p-table da el índice del primer registro, y el endpoint espera número de página
      const pageSize = $event.rows ?? 50;
      const page = Math.floor(($event.first ?? 0) / pageSize) + 1;

      let sort = '-date';
      if ($event.sortField) sort = ($event.sortOrder > 0 ? '' : '-') + $event.sortField;

      const response = await firstValueFrom(this.apiUsageService.getLog(
        this.userId, this.dateFrom, this.dateTo, page, pageSize, this.logFilters, sort, this.statusFilterQuery));

      this.logItems = response.items;
      this.logTotal = response.total;
    } catch (error) {
      console.warn('No se ha podido cargar el registro de peticiones', error);
      this.logItems = [];
      this.logTotal = 0;
    } finally {
      this.logLoading = false;
    }
  }

  search() {
    if (!this.isApiUser) return;

    this.loadStats();

    if (this.lastGridEvent != null) {
      this.lastGridEvent.first = 0;
      this.loadLog(this.lastGridEvent);
    }
  }

  clearLogFilters() {
    this.logFilters = this.getDefaultLogFilters();
    this.reloadLog();
  }

  /// Recarga solo el registro, desde la primera página. Los filtros de la tabla no afectan a las gráficas
  /// —que son del periodo completo—, así que volver a pedir las estadísticas sería tráfico para nada y
  /// además repintaría la propia tarta que se acaba de pulsar.
  public reloadLog() {
    if (!this.isApiUser) return;
    if (this.lastGridEvent == null) return;

    this.lastGridEvent.first = 0;
    this.loadLog(this.lastGridEvent);
  }

  /// Abre la respuesta que se le devolvió a esa petición. El cuerpo no viene en la lista —una página de 50
  /// filas podría traer 200 000 caracteres que casi nunca se miran—, así que se pide al abrir el detalle.
  async showResponse(item: ApiUsageLogItem) {
    this.responseItem = item;
    this.responseBody = '';
    this.responseNote = '';
    this.responseVisible = true;
    this.responseLoading = true;

    try {
      const response = await firstValueFrom(
        this.apiUsageService.getLogResponse(this.userId, this.dateFrom, this.dateTo, item.id));

      this.responseBody = this.formatResponseBody(response.responseBody);

      if (this.responseBody == '') {
        this.responseNote = 'Esta petición no tiene guardada la respuesta.';
      } else if (response.length >= UsersApiUsageComponent.MAX_RESPONSE_LENGTH) {
        this.responseNote = 'Se guardan como máximo los primeros '
          + UsersApiUsageComponent.MAX_RESPONSE_LENGTH + ' caracteres: esta respuesta puede estar cortada.';
      }
    } catch (error) {
      console.warn('No se ha podido cargar la respuesta de la petición', error);
      this.responseNote = 'No se ha podido cargar la respuesta.';
    } finally {
      this.responseLoading = false;
    }
  }

  /// El cuerpo con sangría si es JSON, y tal cual si no. Las respuestas de error vienen en una sola línea y
  /// sin formatear no hay quien las lea; una respuesta cortada por el tope ya no es JSON válido, y entonces
  /// se muestra en bruto en lugar de no mostrar nada.
  private formatResponseBody(body?: string | null): string {
    if (body == null || body == '') return '';

    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  }

  /// El filtro de código va a todas las consultas, así que cambiarlo recarga la pantalla entera y no solo
  /// el registro.
  onStatusFilterChange() {
    this.search();
  }

  clearStatusFilter() {
    this.statusFilter = null;
    this.search();
  }

  /// Pone el filtro general con la porción pulsada de la tarta. Volver a pulsar la misma lo quita, que es lo
  /// que se espera de un filtro que se pone con un clic.
  onStatusSliceSelect($event: any) {
    //en un periodo ya purgado no hay nada que filtrar: el agregado no guarda el código de cada petición
    if (!this.hasDetailSource) return;

    const index = $event?.element?.index;
    const slice = index != null ? this.statusChartSlices[index] : null;
    if (slice == null) return;

    const value = 'code:' + slice.statusCode;
    this.statusFilter = this.statusFilter == value ? null : value;
    this.search();
  }

  /// El filtro en la forma que esperan los endpoints. Se manda el código o la clase, nunca los dos.
  public get statusFilterQuery(): ApiUsageStatusFilter {
    if (this.statusFilter == null) return {};

    const separator = this.statusFilter.indexOf(':');
    const value = parseInt(this.statusFilter.substring(separator + 1), 10);

    return this.statusFilter.substring(0, separator) == 'class'
      ? { statusCodeClass: value }
      : { statusCode: value };
  }

  /// Opciones del filtro a partir del reparto del periodo: solo se ofrece lo que existe. Las clases salen de
  /// los códigos presentes, y con el detalle purgado el reparto ya viene por clases, así que entonces no hay
  /// grupo de códigos.
  private buildStatusFilterOptions() {
    const classes: any[] = [];
    const codes: any[] = [];

    for (const slice of [...this.statusSlices].sort((a, b) => a.statusCode - b.statusCode)) {
      //el 0 no tiene clase: es la petición que el cliente abortó, y solo se puede filtrar por código
      const statusClass = Math.floor(slice.statusCode / 100);
      const classValue = 'class:' + statusClass;

      if (statusClass > 0 && !classes.some((option) => option.value == classValue)) {
        classes.push({ value: classValue, name: UsersApiUsageComponent.STATUS_CLASS_NAMES[statusClass] ?? statusClass + 'xx' });
      }

      //sin detalle el reparto ya viene por clases, así que no hay códigos concretos que ofrecer
      if (this.hasDetailSource) codes.push({ value: 'code:' + slice.statusCode, name: slice.label });
    }

    const groups: any[] = [];
    if (classes.length > 0) groups.push({ name: 'Por clase', items: classes });
    if (codes.length > 0) groups.push({ name: 'Por código', items: codes });

    //el filtro puesto puede no existir en el periodo nuevo —o venir de un periodo con detalle y estar ahora
    //sobre uno purgado—: se conserva como opción para que el desplegable no se quede en blanco mientras
    //sigue filtrando, y para que se pueda quitar desde él
    if (this.statusFilter != null && !groups.some((group) => group.items.some((option: any) => option.value == this.statusFilter))) {
      groups.push({ name: 'Filtro puesto', items: [{ value: this.statusFilter, name: this.describeStatusFilter(this.statusFilter) }] });
    }

    this.statusFilterOptions = groups;
  }

  /// Rótulo del filtro activo, para el distintivo de la tarta y la nota del periodo. Cadena vacía si no hay.
  public get statusFilterLabel(): string {
    return this.statusFilter == null ? '' : this.describeStatusFilter(this.statusFilter);
  }

  private describeStatusFilter(value: string): string {
    const separator = value.indexOf(':');
    const kind = value.substring(0, separator);
    const code = value.substring(separator + 1);

    if (kind == 'class') return UsersApiUsageComponent.STATUS_CLASS_NAMES[code] ?? code + 'xx';

    const slice = this.statusSlices.find((item) => item.statusCode == parseInt(code, 10));
    return slice != null ? slice.label : code;
  }

  /// El periodo no puede responder al filtro de código: su detalle ya se purgó y el agregado diario no
  /// guarda el código de cada petición. Sin esto, los indicadores a cero se leerían como "no hubo tráfico".
  public get isStatusFilterUnavailable(): boolean {
    return this.summary != null && this.summary.source == 'unavailable';
  }

  /// Si el periodo tiene detalle, que es lo que hace posible filtrar por código. No vale preguntar por
  /// isAggregatedSource: con el filtro puesto el origen que llega es 'unavailable', que también significa
  /// que no hay detalle.
  public get hasDetailSource(): boolean {
    return this.summary != null && this.summary.source == 'detail';
  }

  /// Gráfica de evolución, común a las tres granularidades. Hasta MAX_BAR_POINTS son columnas con una sola
  /// tinta, más oscura cuanto mayor el valor: es magnitud, no identidad. Por encima son demasiados puntos
  /// para columnas y pasa a línea.
  buildEvolutionChart() {
    const points = this.evolutionPoints();
    const labels = points.map((point) => point.label);
    const values = points.map((point) => point.value);

    const max = values.length > 0 ? Math.max(...values) : 0;
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color-secondary')?.trim() || '#6c757d';
    const gridColor = documentStyle.getPropertyValue('--surface-border')?.trim() || '#dee2e6';

    const unit = this.seriesMode == 'duration' ? ' ms de media' : (this.seriesMode == 'errors' ? ' errores' : ' peticiones');
    const asLine = values.length > UsersApiUsageComponent.MAX_BAR_POINTS;

    this.evolutionChartType = asLine ? 'line' : 'bar';

    this.evolutionChartData = asLine
      ? {
        labels: labels,
        datasets: [{
          data: values,
          borderColor: UsersApiUsageComponent.LINE_COLOR,
          backgroundColor: UsersApiUsageComponent.LINE_COLOR,
          borderWidth: 1.5,
          //con cientos de puntos los marcadores se solapan en una banda sólida y tapan la forma
          pointRadius: 0,
          tension: 0,
          fill: false
        }]
      }
      : {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: values.map((value) => this.sequentialColor(value, max)),
          borderRadius: 4,
          borderSkipped: 'bottom',
          barPercentage: 0.7,
          categoryPercentage: 0.9
        }]
      };

    this.evolutionChartOptions = {
      maintainAspectRatio: false,
      //el modo por defecto busca el punto más cercano en los dos ejes y con cientos de puntos el tooltip se
      //vuelve errático; 'index' engancha siempre a la vertical que está bajo el cursor
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: any[]) => items.length > 0 ? (points[items[0].dataIndex]?.tooltip ?? '') : '',
            label: (context: any) => ' ' + context.parsed.y + unit
          }
        }
      },
      scales: {
        x: { ticks: { color: textColor, maxRotation: 0, autoSkipPadding: 12 }, grid: { display: false } },
        y: { beginAtZero: true, ticks: { color: textColor, precision: this.seriesMode == 'duration' ? 1 : 0 }, grid: { color: gridColor } }
      }
    };
  }

  /// Aplana la granularidad activa a etiqueta, valor y título de tooltip, para que la construcción de la
  /// gráfica no tenga que saber de dónde salen los puntos.
  private evolutionPoints(): { label: string, value: number, tooltip: string }[] {
    if (this.granularity == 'hour') {
      //la fecha solo se repite en la etiqueta si el rango abarca más de un día UTC
      const singleDay = this.isSingleUtcDayRange;

      return this.hourlySeries.map((point) => {
        const date = point.hour instanceof Date ? point.hour : new Date(point.hour);
        const hour = date.getUTCHours();
        const day = this.formatDay(date);

        return {
          label: singleDay ? this.formatHour(hour) : day + ' ' + this.formatHour(hour),
          value: this.seriesValue(point),
          tooltip: day + ' ' + this.formatHourRange(hour)
        };
      });
    }

    if (this.granularity == 'hourOfDay') {
      return this.hourOfDay.map((point) => ({
        label: this.formatHour(point.hour),
        value: this.seriesValue(point),
        tooltip: this.formatHourRange(point.hour)
      }));
    }

    return this.daily.map((point) => ({
      label: this.formatDay(point.day),
      value: this.seriesValue(point),
      tooltip: this.formatDay(point.day)
    }));
  }

  private seriesValue(point: { requests: number, errors: number, averageDurationMs: number }): number {
    if (this.seriesMode == 'errors') return point.errors;
    if (this.seriesMode == 'duration') return point.averageDurationMs;
    return point.requests;
  }

  /// Tarta del reparto por código de estado. El color va por clase de estado y el valor y el porcentaje van
  /// en el texto de la leyenda: hay tonos que no llegan a 3:1 sobre el fondo y la identidad no puede
  /// depender solo del color.
  buildStatusChart() {
    const total = this.statusSlices.reduce((sum, slice) => sum + slice.requests, 0);

    const sorted = [...this.statusSlices].sort((a, b) => b.requests - a.requests);
    this.statusChartSlices = sorted;
    const labels = sorted.map((slice) => slice.label);
    const values = sorted.map((slice) => slice.requests);
    const colors = sorted.map((slice) => this.statusColor(slice.statusCode));

    const documentStyle = getComputedStyle(document.documentElement);
    const surface = documentStyle.getPropertyValue('--surface-card')?.trim() || '#ffffff';
    const textColor = documentStyle.getPropertyValue('--text-color')?.trim() || '#495057';

    this.statusChartData = {
      labels: labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        //la tarta se muestra siempre completa, porque es el selector del filtro: la porción filtrada sale
        //desplazada para que se vea cuál está elegida
        offset: sorted.map((slice) => this.isSliceFiltered(slice) ? 16 : 0),
        //separación del color de la superficie entre porciones
        borderColor: surface,
        borderWidth: 2
      }]
    };

    this.statusChartOptions = {
      cutout: '58%',
      maintainAspectRatio: false,
      //la porción pone el filtro al pulsarla, así que tiene que verse pulsable. En un periodo purgado no
      //se puede filtrar, y entonces tampoco tiene que parecer pulsable
      onHover: (event: any, elements: any[]) => {
        const canvas = event?.native?.target;
        if (canvas == null) return;

        canvas.style.cursor = elements.length > 0 && this.hasDetailSource ? 'pointer' : 'default';
      },
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: textColor,
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: 'rectRounded',
            generateLabels: (chart: any) => {
              return chart.data.labels.map((label: string, i: number) => {
                const value = chart.data.datasets[0].data[i];
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return {
                  text: label + ' — ' + value + ' (' + pct + '%)',
                  fillStyle: chart.data.datasets[0].backgroundColor[i],
                  strokeStyle: chart.data.datasets[0].backgroundColor[i],
                  lineWidth: 0,
                  index: i
                };
              });
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const pct = total > 0 ? Math.round((context.parsed / total) * 100) : 0;
              return ' ' + context.label + ': ' + context.parsed + ' (' + pct + '%)';
            }
          }
        }
      }
    };
  }

  /// Si la porción cae dentro del filtro puesto. Con un filtro por clase son varias las que caen.
  private isSliceFiltered(slice: ApiUsageStatusSlice): boolean {
    if (this.statusFilter == null) return false;

    const query = this.statusFilterQuery;
    return query.statusCode != null
      ? slice.statusCode == query.statusCode
      : Math.floor(slice.statusCode / 100) == query.statusCodeClass;
  }

  private statusColor(statusCode: number): string {
    const statusClass = Math.floor(statusCode / 100).toString();
    return UsersApiUsageComponent.STATUS_COLORS[statusClass] ?? UsersApiUsageComponent.STATUS_COLORS['0'];
  }

  private sequentialColor(value: number, max: number): string {
    const t = max > 0 ? value / max : 0;
    const low = UsersApiUsageComponent.SEQUENTIAL_LOW;
    const high = UsersApiUsageComponent.SEQUENTIAL_HIGH;
    const channel = (i: number) => Math.round(low[i] + (high[i] - low[i]) * t);
    return 'rgb(' + channel(0) + ',' + channel(1) + ',' + channel(2) + ')';
  }

  /// Cambiar la serie no vuelve al servidor: se recalcula con lo que ya está cargado.
  onSeriesModeChange() {
    this.buildEvolutionChart();
  }

  private formatDay(day: Date | string): string {
    const date = day instanceof Date ? day : new Date(day);
    return ('0' + date.getUTCDate()).slice(-2) + '/' + ('0' + (date.getUTCMonth() + 1)).slice(-2);
  }

  /// Las horas se agrupan en UTC, igual que los días, así que se leen con getUTCHours y el tooltip lo
  /// rotula.
  private formatHour(hour: number): string {
    return ('0' + hour).slice(-2) + 'h';
  }

  /// Título del tooltip: el cubo entero, para que no se lea como un instante.
  private formatHourRange(hour: number): string {
    const start = ('0' + hour).slice(-2);
    return start + ':00–' + start + ':59 UTC';
  }

  /// Si el rango cabe en un solo día UTC. Ojo: el rango se construye sobre el día natural local, así que
  /// "Hoy" visto en hora peninsular abarca dos días UTC y esto devuelve false, que es justo lo que se
  /// quiere —las etiquetas tienen que decir de qué día UTC es cada hora—.
  private get isSingleUtcDayRange(): boolean {
    return this.dateFrom.getUTCFullYear() == this.dateTo.getUTCFullYear()
      && this.dateFrom.getUTCMonth() == this.dateTo.getUTCMonth()
      && this.dateFrom.getUTCDate() == this.dateTo.getUTCDate();
  }

  /// Porcentaje de error con un decimal, para la tarjeta de indicadores.
  public get errorRatePercent(): string {
    if (this.summary == null) return '0';
    return (this.summary.errorRate * 100).toFixed(1);
  }

  /// Indica si los datos vienen del agregado, es decir, si el detalle de esos días ya se purgó.
  public get isAggregatedSource(): boolean {
    return this.summary != null && this.summary.source == 'daily';
  }

  /// El perfil por hora del día necesita el detalle: el agregado perpetuo no guarda la hora. Privado:
  /// nadie fuera de applyGranularityOptionState() y granularityNote lo necesita.
  private get isHourOfDayAvailable(): boolean {
    return !this.isAggregatedSource;
  }

  /// La serie horaria necesita además un rango que el servidor sirva. Privado, por el mismo motivo.
  private get isHourlySeriesAvailable(): boolean {
    return !this.isAggregatedSource && this.rangeDaysSpan <= UsersApiUsageComponent.MAX_HOURLY_SERIES_DAYS;
  }

  /// Días que abarca el rango elegido, con decimales.
  private get rangeDaysSpan(): number {
    return (this.dateTo.getTime() - this.dateFrom.getTime()) / 86400000;
  }

  /// Motivo por el que una granularidad horaria está deshabilitada, para la nota al pie. Cadena vacía
  /// cuando las dos están disponibles.
  public get granularityNote(): string {
    if (this.isAggregatedSource) return 'No hay desglose por hora en este periodo: el detalle de esas fechas ya se ha purgado.';
    if (!this.isHourlySeriesAvailable) return 'La serie por hora necesita un periodo de ' + UsersApiUsageComponent.MAX_HOURLY_SERIES_DAYS + ' días o menos.';
    return '';
  }

  /// Texto a mostrar en lugar de la gráfica cuando la vista activa no se pudo cargar.
  public get evolutionError(): string {
    if (this.granularity == 'hour' && this.hourlyError) return 'No se ha podido cargar la serie por hora de este periodo. Pulsa Buscar para volver a intentarlo.';
    if (this.granularity == 'hourOfDay' && this.hourOfDayError) return 'No se ha podido cargar el perfil por hora del día de este periodo. Pulsa Buscar para volver a intentarlo.';
    return '';
  }

  public get evolutionTitle(): string {
    if (this.granularity == 'hour') return 'Evolución por hora';
    if (this.granularity == 'hourOfDay') return 'Perfil por hora del día';
    return 'Evolución por día';
  }

  /// El tipo literal es el que espera la propiedad severity de p-tag.
  public statusSeverity(statusCode: number): 'success' | 'warn' | 'danger' | 'secondary' {
    if (statusCode == 0) return 'secondary';
    if (statusCode >= 500) return 'danger';
    if (statusCode >= 400) return 'warn';
    return 'success';
  }
}
